






























































import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { ClientPartner, PARTNER_SYSTEM_CHOICES, PartnerSystemChoice } from "@/data/models/ClientPartner";
import { createClientPartner } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { isPartnerEmail, isValidPartnerAddress, partnerErrorKey, partnerSystemLabelKey, proposedPartnerEmail } from "@/utils/ClientPartners";

/** What `show()` resolves with: the new system and its password (shown once), or null. */
export type PartnerCreateResult = { partner: ClientPartner; password: string } | null;

const PARTNERS = "admin.clients.partners";

@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ClientPartnerCreateDialog extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  clientId = "";
  clientPrefix: string | null = null;
  name = "";
  /** Follows the proposal until typed by hand. */
  typedEmail: string | null = null;
  system: PartnerSystemChoice = "EXTERNAL";
  signIn: "API_KEY" | "SIGN_IN" = "API_KEY";
  baseUrl = "";
  emailTaken = false;

  private resolve: ((result: PartnerCreateResult) => void) | null = null;

  get email(): string {
    return this.typedEmail ?? proposedPartnerEmail(this.clientPrefix, this.name);
  }

  get systemItems() {
    return PARTNER_SYSTEM_CHOICES.map((value) => ({ value, label: this.$t(partnerSystemLabelKey(value === "NONE" ? null : value)).toString() }));
  }

  get signInItems() {
    return (["API_KEY", "SIGN_IN"] as const).map((value) => ({ value, label: this.$t(`${PARTNERS}.signIn.${value}`).toString() }));
  }

  get emailError(): string {
    if (this.emailTaken) {
      return this.$t(`${PARTNERS}.errors.EMAIL_TAKEN`).toString();
    }
    return this.email.trim() !== "" && !isPartnerEmail(this.email) ? this.$t(`${PARTNERS}.create.emailInvalid`).toString() : "";
  }

  get baseUrlError(): string {
    return isValidPartnerAddress(this.baseUrl) ? "" : this.$t(`${PARTNERS}.fieldErrors.baseUrl`).toString();
  }

  get hasInput(): boolean {
    return this.name.trim() !== "" || this.typedEmail !== null || this.baseUrl.trim() !== "";
  }

  get isComplete(): boolean {
    return this.name.trim() !== "" && isPartnerEmail(this.email) && !this.emailTaken && this.baseUrlError === "";
  }

  onEmailInput(value: string) {
    this.typedEmail = value;
    this.emailTaken = false;
  }

  /**
   * @param clientId - the client that will own it (never a group member).
   * @param clientPrefix - its Kürzel, for the proposed sign-in address.
   */
  show(clientId: string, clientPrefix: string | null): Promise<PartnerCreateResult> {
    this.clientId = clientId;
    this.clientPrefix = clientPrefix;
    this.name = "";
    this.typedEmail = null;
    this.system = "EXTERNAL";
    this.signIn = "API_KEY";
    this.baseUrl = "";
    this.emailTaken = false;
    this.baseDialog.show({
      title: this.$t(`${PARTNERS}.create.title`).toString(),
      positiveButtonText: this.$t(`${PARTNERS}.create.button`).toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 560,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onNegative() {
    if (!this.hasInput) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onCreate() {
    if (!this.isComplete) {
      return;
    }
    this.busy = true;
    try {
      const created = await createClientPartner(this.clientId, {
        name: this.name.trim(),
        email: this.email.trim().toLowerCase(),
        system: this.system,
        signIn: this.system === "EXTERNAL" ? this.signIn : null,
        baseUrl: this.system === "NONE" || this.baseUrl.trim() === "" ? null : this.baseUrl.trim(),
      });
      SnackbarModule.showSuccess({ key: `${PARTNERS}.create.created` });
      this.close(created);
    } catch (error) {
      const key = partnerErrorKey(error);
      if (key.endsWith("EMAIL_TAKEN")) {
        this.emailTaken = true;
      }
      SnackbarModule.showError({ key });
    } finally {
      this.busy = false;
    }
  }

  close(result: PartnerCreateResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: PartnerCreateResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
